<template>
  <div class="scroll-progress" aria-hidden="true">
    <div ref="barEl" class="scroll-progress__fill"/>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';

// Replaces the native scrollbar (hidden globally in app.vue) with a bottom
// progress bar. Because the pages are pinned horizontal tracks, vertical
// scroll position IS the progress through the panels, so plain document
// scroll maths works on both pages and in the mobile vertical fallback.
const barEl = ref(null);
let frame = null;

// How far there is to scroll, cached rather than measured every frame.
//
// Reading scrollHeight forces a synchronous layout, and doing that once per
// scroll frame is wasteful on its own. On Android Chrome it is worse than
// wasteful: while the address bar is sliding away, both scrollHeight and
// innerHeight are changing, so the bar was dividing a moving numerator by a
// moving denominator and jittering through the whole transition.
//
// The document's height only changes when something reflows, which is what
// resize is for.
let maxScroll = 0;

function measure() {
  maxScroll = document.documentElement.scrollHeight - window.innerHeight;
}

function render() {
  frame = null;
  const el = barEl.value;
  if (!el) return;

  const max = maxScroll;
  const progress = max > 0 ? Math.min(Math.max(window.scrollY / max, 0), 1) : 0;

  // scaleX rather than width: it's a compositor-only property, so updating it
  // every scroll frame costs no layout or paint.
  el.style.transform = `scaleX(${progress})`;
}

function onScroll() {
  if (frame === null) frame = requestAnimationFrame(render);
}

function onResize() {
  measure();
  onScroll();
}

onMounted(() => {
  measure();
  render();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  // The pinned track and any late images change the document's height after
  // this component mounts, so the first measurement is taken again once the
  // page has settled.
  window.addEventListener('load', measure, { once: true });
  setTimeout(measure, 1500);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onResize);
  if (frame !== null) cancelAnimationFrame(frame);
});
</script>

<style lang="scss" scoped>
.scroll-progress {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  z-index: 1500;
  pointer-events: none;
  background: rgba(var(--muted-rgb), 0.16);
}

.scroll-progress__fill {
  width: 100%;
  height: 100%;
  transform: scaleX(0);
  transform-origin: left center;
  background: linear-gradient(
    90deg,
    rgba(var(--accent-rgb), 0.55) 0%,
    var(--accent) 60%,
    rgba(var(--accent-rgb), 0.9) 100%
  );
}
</style>
